// Real Stripe API Integration Service
class RealStripeService {
  constructor() {
    this.publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
    this.secretKey = process.env.REACT_APP_STRIPE_SECRET_KEY;
    this.stripe = null;
    this.initStripe();
  }

  // Initialize Stripe
  initStripe() {
    if (typeof window !== 'undefined' && window.Stripe) {
      this.stripe = window.Stripe(this.publishableKey);
    }
  }

  // Create payment intent
  async createPaymentIntent(amount, currency = 'usd', metadata = {}) {
    try {
      const response = await fetch('/api/create-payment-intent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: Math.round(amount * 100), // Convert to cents
          currency,
          metadata
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create payment intent');
      }

      const data = await response.json();
      return data.clientSecret;
    } catch (error) {
      console.error('Error creating payment intent:', error);
      throw error;
    }
  }

  // Process payment with Stripe
  async processPayment(paymentMethodId, amount, currency = 'usd', metadata = {}) {
    try {
      if (!this.stripe) {
        throw new Error('Stripe not initialized');
      }

      // Create payment intent
      const clientSecret = await this.createPaymentIntent(amount, currency, metadata);

      // Confirm payment
      const result = await this.stripe.confirmCardPayment(clientSecret, {
        payment_method: paymentMethodId
      });

      if (result.error) {
        throw new Error(result.error.message);
      }

      return {
        success: true,
        paymentIntentId: result.paymentIntent.id,
        amount: amount,
        currency: currency,
        status: result.paymentIntent.status
      };
    } catch (error) {
      console.error('Error processing payment:', error);
      throw error;
    }
  }

  // Create customer
  async createCustomer(email, name, phone = null) {
    try {
      const response = await fetch('/api/create-customer', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          name,
          phone
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create customer');
      }

      const data = await response.json();
      return data.customer;
    } catch (error) {
      console.error('Error creating customer:', error);
      throw error;
    }
  }

  // Save payment method
  async savePaymentMethod(paymentMethodId, customerId) {
    try {
      const response = await fetch('/api/save-payment-method', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          paymentMethodId,
          customerId
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save payment method');
      }

      const data = await response.json();
      return data.paymentMethod;
    } catch (error) {
      console.error('Error saving payment method:', error);
      throw error;
    }
  }

  // Get customer payment methods
  async getCustomerPaymentMethods(customerId) {
    try {
      const response = await fetch(`/api/customer-payment-methods/${customerId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error('Failed to get payment methods');
      }

      const data = await response.json();
      return data.paymentMethods;
    } catch (error) {
      console.error('Error getting payment methods:', error);
      throw error;
    }
  }

  // Create subscription
  async createSubscription(customerId, priceId, metadata = {}) {
    try {
      const response = await fetch('/api/create-subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customerId,
          priceId,
          metadata
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create subscription');
      }

      const data = await response.json();
      return data.subscription;
    } catch (error) {
      console.error('Error creating subscription:', error);
      throw error;
    }
  }

  // Cancel subscription
  async cancelSubscription(subscriptionId) {
    try {
      const response = await fetch(`/api/cancel-subscription/${subscriptionId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error('Failed to cancel subscription');
      }

      const data = await response.json();
      return data.subscription;
    } catch (error) {
      console.error('Error canceling subscription:', error);
      throw error;
    }
  }

  // Get payment history
  async getPaymentHistory(customerId, limit = 10) {
    try {
      const response = await fetch(`/api/payment-history/${customerId}?limit=${limit}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error('Failed to get payment history');
      }

      const data = await response.json();
      return data.payments;
    } catch (error) {
      console.error('Error getting payment history:', error);
      throw error;
    }
  }

  // Refund payment
  async refundPayment(paymentIntentId, amount = null, reason = 'requested_by_customer') {
    try {
      const response = await fetch('/api/refund-payment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          paymentIntentId,
          amount,
          reason
        })
      });

      if (!response.ok) {
        throw new Error('Failed to refund payment');
      }

      const data = await response.json();
      return data.refund;
    } catch (error) {
      console.error('Error refunding payment:', error);
      throw error;
    }
  }

  // Create invoice
  async createInvoice(customerId, items, metadata = {}) {
    try {
      const response = await fetch('/api/create-invoice', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customerId,
          items,
          metadata
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create invoice');
      }

      const data = await response.json();
      return data.invoice;
    } catch (error) {
      console.error('Error creating invoice:', error);
      throw error;
    }
  }

  // Send invoice
  async sendInvoice(invoiceId) {
    try {
      const response = await fetch(`/api/send-invoice/${invoiceId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error('Failed to send invoice');
      }

      const data = await response.json();
      return data.invoice;
    } catch (error) {
      console.error('Error sending invoice:', error);
      throw error;
    }
  }

  // Get Stripe elements
  getElements() {
    if (!this.stripe) {
      throw new Error('Stripe not initialized');
    }
    return this.stripe.elements();
  }

  // Create payment method from card element
  async createPaymentMethodFromElement(elements, billingDetails = {}) {
    try {
      if (!this.stripe) {
        throw new Error('Stripe not initialized');
      }

      const result = await this.stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement('card'),
        billing_details: billingDetails
      });

      if (result.error) {
        throw new Error(result.error.message);
      }

      return result.paymentMethod;
    } catch (error) {
      console.error('Error creating payment method:', error);
      throw error;
    }
  }

  // Demo payment fallback
  async processDemoPayment(paymentData) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          success: true,
          paymentIntentId: `pi_demo_${Date.now()}`,
          amount: paymentData.amount,
          currency: paymentData.currency || 'usd',
          status: 'succeeded'
        });
      }, 1000);
    });
  }
}

// Create singleton instance
const realStripeService = new RealStripeService();

export default realStripeService; 